import { Alert, Box, Button, Chip, Grid2 as Grid, IconButton, Snackbar, Tooltip, Typography } from '@mui/material';
import React, { FC, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useAuth } from 'oidc-react';
import SettingsIcon from '@mui/icons-material/Settings';
import HistoryIcon from '@mui/icons-material/History';
import { Link as RouterLink } from 'react-router-dom';
import { Revision } from './Details';
import { StyledPaper } from '../theme';
import { ChecklistPhases, stateLabels } from './ChecklistTasks';
import { ChecklistTaskState } from '../__generated__/graphql';
import { SxProps, Theme } from '@mui/material/styles';
import { useCanEditChecklists } from './useCanEditChecklists';
import { MIGRATE_CHECKLIST } from './revisions.graphql';

export const Checklist: FC<{ revision: Revision; history: boolean }> = ({ revision, history }) => {
  const canEditChecklists = useCanEditChecklists();
  const checklist = revision?.checklist;

  return (
    <StyledPaper>
      <Grid container justifyContent="space-between">
        <Grid>
          <Typography variant="h5" component="h3">
            Aktiviteter
          </Typography>
          <Grid container alignItems="center" spacing={1}>
            <Typography variant="caption">Versjon {checklist?.version}</Typography>
            {checklist?.latestVersion !== checklist?.version && (
              <Tooltip title="Revisjonen fullføres på den versjonen den ble startet på, hvis den ikke flyttes.">
                <Chip
                  size="small"
                  variant="outlined"
                  color="warning"
                  icon={<HistoryIcon />}
                  label={`Malen er oppdatert til ${checklist?.latestVersion}`}
                />
              </Tooltip>
            )}
            {!history && checklist?.latestVersion !== checklist?.version && <MigrateButton revision={revision} />}
          </Grid>
        </Grid>
        <Grid container alignItems="flex-start" spacing={1}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h4" component="p" color="primary" sx={{ fontWeight: 500, lineHeight: 1 }}>
              {revision?.progress ?? 0} %
            </Typography>
            <Typography variant="caption" color="textSecondary">
              fremdrift
            </Typography>
          </Box>
          {canEditChecklists && (
            <IconButton component={RouterLink} to={`/checklist-template?type=${revision?.type}&revision=${revision?.id}`} title="Tilpass malen" size="small">
              <SettingsIcon fontSize="small" />
            </IconButton>
          )}
        </Grid>
      </Grid>
      <ProgressBar milestones={checklist?.milestones} />
      <ChecklistPhases revisionId={revision?.id} milestones={checklist?.milestones} history={history} />
    </StyledPaper>
  );
};


const segmentFill: Record<ChecklistTaskState, SxProps<Theme>> = {
  [ChecklistTaskState.Done]: { bgcolor: 'primary.main' },
  [ChecklistTaskState.NotRelevant]: { backgroundImage: 'repeating-linear-gradient(135deg, #bdbdbd 0 3px, #eeeeee 3px 6px)' },
  [ChecklistTaskState.Started]: { bgcolor: 'warning.main' },
  [ChecklistTaskState.NotStarted]: { bgcolor: 'grey.300' },
};

/** Done and not relevant first: together they are the progress figure above the bar. */
const segmentOrder = [ChecklistTaskState.Done, ChecklistTaskState.NotRelevant, ChecklistTaskState.Started, ChecklistTaskState.NotStarted];

type Milestones = NonNullable<NonNullable<Revision>['checklist']>['milestones'];

const ProgressBar: FC<{ milestones: Milestones }> = ({ milestones }) => {
  const tasks = (milestones ?? []).flatMap((milestone) => milestone?.tasks ?? []).filter((task) => !!task);
  const total = tasks.reduce((sum, task) => sum + task.weight, 0);
  const shareOf = (state: ChecklistTaskState) => (total > 0 ? (tasks.filter((task) => task.state === state).reduce((sum, task) => sum + task.weight, 0) / total) * 100 : 0);

  return (
    <Box role="img" aria-label="Fremdrift per tilstand" sx={{ mt: 1.5, display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', backgroundColor: 'grey.300' }}>
      {segmentOrder
        .map((state) => ({ state, share: shareOf(state) }))
        .filter((segment) => segment.share > 0)
        .map((segment) => (
          <Tooltip key={segment.state} title={`${stateLabels[segment.state]}: ${Math.round(segment.share)} %`}>
            <Box sx={[{ width: `${segment.share}%` }, segmentFill[segment.state]] as SxProps<Theme>} />
          </Tooltip>
        ))}
    </Box>
  );
};

const MigrateButton: FC<{ revision: Revision }> = ({ revision }) => {
  const auth = useAuth();
  const canEditChecklists = useCanEditChecklists();
  const [migrate, { loading }] = useMutation(MIGRATE_CHECKLIST);
  const [error, setError] = useState<string | null>(null);

  const subjectId = auth.userData?.profile.sub;
  const isProjectLead = (revision?.projectLeads ?? []).some((lead) => lead?.id === subjectId);

  if (!canEditChecklists && !isProjectLead) return null;

  const handleMigrate = async () => {
    setError(null);
    const result = await migrate({ variables: { input: { id: revision?.id } } });

    const failure = result.data?.revisionMigrateChecklist?.errors?.[0];
    if (failure) setError('message' in failure ? failure.message : 'Kunne ikke flytte revisjonen.');
  };

  return (
    <>
      <Button size="small" disabled={loading} onClick={handleMigrate}>
        Flytt til {revision?.checklist?.latestVersion}
      </Button>
      <Snackbar open={!!error} autoHideDuration={10000} onClose={() => setError(null)}>
        <Alert severity="warning" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};
